import React from "react"

// Types
export interface Step {
  label: string
  sublabel?: string
  icon: React.ReactNode
}

export interface ProgressTrackerProps {
  steps: Step[]
  currentStep: number
  orientation?: "horizontal" | "vertical"
  className?: string
  stepClassName?: string
  labelClassName?: string
  sublabelClassName?: string
  iconClassName?: string
  connectorClassName?: string
  activeConnectorClassName?: string
  inactiveConnectorClassName?: string
  activeColor?: string
  inactiveColor?: string
  showLabels?: boolean
  showConnectors?: boolean
  connectorStyle?: "solid" | "dashed" | "dotted"
  size?: "small" | "medium" | "large"
  renderCustomStep?: (step: Step, index: number, isCompleted: boolean, isActive: boolean) => React.ReactNode
}

export const ProgressTracker: React.FC<ProgressTrackerProps> = ({
  steps,
  currentStep,
  orientation = "horizontal",
  className = "",
  stepClassName = "",
  labelClassName = "",
  sublabelClassName = "",
  iconClassName = "",
  connectorClassName = "",
  activeConnectorClassName = "",
  inactiveConnectorClassName = "",
  activeColor = "#0074D9",
  inactiveColor = "#E1E8ED",
  showLabels = true,
  showConnectors = true,
  connectorStyle = "solid",
  size = "medium",
  renderCustomStep,
}) => {
  // Size mappings
  const sizeMap = {
    small: { icon: 24, fontSize: 12, subFontSize: 10, spacing: 8 },
    medium: { icon: 32, fontSize: 14, subFontSize: 12, spacing: 12 },
    large: { icon: 48, fontSize: 16, subFontSize: 14, spacing: 16 },
  }

  const currentSize = sizeMap[size]
  const verticalStepSpacing = 32 // Space between vertical steps

  // Base styles
  const baseStyles = {
    container: {
      display: "flex",
      flexDirection: orientation === "horizontal" ? ("column" as const) : ("row" as const),
      width: "100%",
      padding: "16px",
      boxSizing: "border-box" as const,
    },
    stepsWrapper: {
      display: "flex",
      flexDirection: orientation === "horizontal" ? ("row" as const) : ("column" as const),
      width: "100%",
      position: "relative" as const,
    },
    step: {
      display: "flex",
      flexDirection: orientation === "horizontal" ? ("column" as const) : ("row" as const),
      alignItems: "center",
      justifyContent: orientation === "horizontal" ? "flex-start" : "flex-start",
      position: "relative" as const,
      zIndex: 2,
    },
    stepIcon: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      width: `${currentSize.icon}px`,
      height: `${currentSize.icon}px`,
      borderRadius: "50%",
      backgroundColor: inactiveColor,
      color: "#FFFFFF",
      transition: "all 0.3s ease",
    },
    stepIconActive: {
      backgroundColor: activeColor,
    },
    stepIconCompleted: {
      backgroundColor: activeColor,
    },
    labelContainer: {
      display: "flex",
      flexDirection: "column" as const,
      alignItems: orientation === "horizontal" ? "center" : "flex-start",
      marginTop: orientation === "horizontal" ? `${currentSize.spacing}px` : "0",
      marginLeft: orientation === "horizontal" ? "0" : `${currentSize.spacing}px`,
    },
    label: {
      fontSize: `${currentSize.fontSize}px`,
      fontWeight: 500,
      textAlign: orientation === "horizontal" ? ("center" as const) : ("left" as const),
      color: "#333333",
    },
    sublabel: {
      fontSize: `${currentSize.subFontSize}px`,
      color: "#666666",
      marginTop: "2px",
    },
    connector: {
      position: "absolute" as const,
      transition: "all 0.3s ease",
      ...(connectorStyle === "dashed" && { borderStyle: "dashed" }),
      ...(connectorStyle === "dotted" && { borderStyle: "dotted" }),
    },
  }

  // Render the connectors separately for better control
  const renderConnectors = () => {
    if (!showConnectors) return null

    return steps.map((_, index) => {
      if (index === steps.length - 1) return null // No connector after last step
      
      const isCompleted = index < currentStep
      
      if (orientation === "horizontal") {
        return (
          <div
            key={`connector-${index}`}
            style={{
              ...baseStyles.connector,
              height: "2px",
              left: `calc(${(index + 0.5) * (100 / steps.length)}%)`,
              right: `calc(${100 - (index + 1.5) * (100 / steps.length)}%)`,
              top: `${currentSize.icon / 2}px`,
              backgroundColor: isCompleted ? activeColor : inactiveColor,
            }}
            className={`${connectorClassName} ${
              isCompleted ? activeConnectorClassName : inactiveConnectorClassName
            }`}
          />
        )
      } else {
        // Vertical connector
        return (
          <div
            key={`connector-${index}`}
            style={{
              ...baseStyles.connector,
              width: "2px",
              top: `${index * (verticalStepSpacing + currentSize.icon) + currentSize.icon}px`,
              height: `${verticalStepSpacing}px`,
              left: `${currentSize.icon / 2 - 1}px`, // Center the connector
              backgroundColor: isCompleted ? activeColor : inactiveColor,
            }}
            className={`${connectorClassName} ${
              isCompleted ? activeConnectorClassName : inactiveConnectorClassName
            }`}
          />
        )
      }
    })
  }

  return (
    <div style={baseStyles.container} className={className}>
      <div style={baseStyles.stepsWrapper}>
        {/* Render connectors first so they appear behind the steps */}
        {renderConnectors()}
        
        {steps.map((step, index) => {
          const isCompleted = index < currentStep
          const isActive = index === currentStep
          const isLast = index === steps.length - 1

          // If custom render function is provided, use it
          if (renderCustomStep) {
            return renderCustomStep(step, index, isCompleted, isActive)
          }

          return (
            <div
              key={`step-${index}`}
              style={{
                ...baseStyles.step,
                ...(orientation === "horizontal" 
                  ? { flex: 1 } 
                  : { marginBottom: isLast ? "0" : `${verticalStepSpacing}px` }),
              }}
              className={stepClassName}
            >
              <div
                style={{
                  ...baseStyles.stepIcon,
                  ...(isActive ? baseStyles.stepIconActive : {}),
                  ...(isCompleted ? baseStyles.stepIconCompleted : {}),
                }}
                className={iconClassName}
              >
                {step.icon}
              </div>

              {showLabels && (
                <div style={baseStyles.labelContainer}>
                  <div style={baseStyles.label} className={labelClassName}>
                    {step.label}
                  </div>
                  {step.sublabel && (
                    <div style={baseStyles.sublabel} className={sublabelClassName}>
                      {step.sublabel}
                    </div>
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default ProgressTracker